import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { Subject } from 'rxjs';

import { SortItemsType } from '@shared/interfaces/list-fields.interface';
import { ListItemFooterDirective } from '@ui-kit/list/item-footer.directive';
import { ListItemHeaderDirective } from '@ui-kit/list/item-header.directive';
import { Collection, CollectionModel } from 'collection';

@Component({
  selector: 'list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListComponent<T> implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {
  protected _cdRef = inject(ChangeDetectorRef);
  protected _destroyed = new Subject();

  @Input() items: Array<T>;
  @Input() fields: Array<SortItemsType>;
  @Input() sumMinWidth = 0;
  @Input() numberFlexColumns = 0;
  @Input() parentWidth: number;
  @Input() collection: Collection | CollectionModel;
  @Input() threeColumnsWidth: WritableSignal<string>;
  @Output() submitClick: EventEmitter<T> = new EventEmitter();
  @ViewChild('listWrap') listWrap: ElementRef;
  @ViewChild('list') list: ElementRef;
  @ContentChild(ListItemHeaderDirective, { static: false, read: TemplateRef }) itemHeaderTemplate;
  @ContentChild(ListItemFooterDirective, { static: false, read: TemplateRef }) itemFooterTemplate;

  templates: {
    [name: string]: any;
  } = {};
  @ContentChildren(TemplateRef, { read: TemplateRef }) fieldTemplates: QueryList<any>;
  @HostBinding('class.hidden') get isHidden(): boolean {
    return !this.items?.length;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this._cdRef.markForCheck();
  }

  clickOnItem(e, field, item): void {
    if (Object.prototype.hasOwnProperty.call(field, 'clickable') && !field.clickable) {
      return;
    }

    if (this.submitClick.observers?.length > 0) {
      this.submitClick.next(item);
    }
  }

  filtersChanged(filter): void {
    this.collection.setParams('page', 1);
    filter ? this.collection.setParams('sort', filter) : this.collection.clearParams('sort');
    this.collection.search();
  }

  identify(index, item): number {
    return item.id;
  }

  identifyField(index, item): string | number {
    return item.key;
  }

  ngOnInit(): void {
    if (this.collection) {
      this.collection.setProperty('view', this._cdRef);
    }
  }

  ngOnDestroy(): void {
    this._destroyed.next(true);
    this._destroyed.complete();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.list && this.parentWidth && this.sumMinWidth && this.numberFlexColumns) {
        this.fields?.forEach((field) => {
          if (field?.isResizable && field?.styles?.flex) {
            const calculated = (this.parentWidth - this.sumMinWidth - 10) / this.numberFlexColumns;
            const sum = calculated + field?.styles?.min;
            const result = calculated > 0 ? sum : field?.styles?.min;
            field.styles.width = result;
          }
        });
      }
    }, 0);
  }

  ngAfterContentInit(): void {
    if (this.fieldTemplates instanceof QueryList) {
      this.templates = this.fieldTemplates.reduce((templates, template, index) => {
        if (template._declarationTContainer.hasOwnProperty('localNames') && template._declarationTContainer.localNames) {
          const templateName = template._declarationTContainer?.localNames[0];
          templates[templateName] = template;
          return templates;
        }
      }, {});

      if (this.templates) {
        this.fields?.forEach((item) => {
          if (this.templates[item.key + 'Field']) {
            item['html'] = this.templates[item.key + 'Field'];
          }
        });
      }
    }
  }
}
